import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import ReportModal from '../components/ReportModal'
import { useAuth, displayUser } from '../context/AuthContext'
import { useBlocks } from '../hooks/useBlocks'
import { supabase } from '../lib/supabase'
import { resizeImage } from '../lib/resizeImage'
import { decodeEntities } from '../lib/decodeEntities'
import SEO from '../components/SEO'

const RESIZE_FALLBACK_MSG = "We couldn't process that image. Try a different photo, or save it as JPEG and retry."

const CATEGORIES = ['General', 'Social', 'Writing', 'Fanfiction', 'Anime', 'Books', 'TV & Film', 'Music', 'Gaming', 'Art', 'LGBTQ+', 'Other']

const GRADIENTS = [
  'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
  'linear-gradient(135deg, #0D9488 0%, #1D4ED8 100%)',
  'linear-gradient(135deg, #EA580C 0%, #EAB308 100%)',
  'linear-gradient(135deg, #BE123C 0%, #7C3AED 100%)',
  'linear-gradient(135deg, #16A34A 0%, #0D9488 100%)',
  'linear-gradient(135deg, #1D4ED8 0%, #EC4899 100%)',
  'linear-gradient(135deg, #374151 0%, #7C3AED 100%)',
  'linear-gradient(135deg, #B45309 0%, #DC2626 100%)',
]

const EMOJIS = ['✨', '📚', '🎭', '🌸', '⚡', '🎵', '🌙', '🔥', '🌊', '🎨', '💫', '🦋', '🗡️', '🌿', '🎮', '🏆']

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso)) / 1000
  if (diff < 60)   return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

export default function GroupPage() {
  const { id }       = useParams()
  const navigate     = useNavigate()
  const { user, profile } = useAuth()
  const currentUser  = displayUser(user, profile)
  const blockedIds = useBlocks()

  const [groupData, setGroupData]   = useState(null)   // { group, recentMembers }
  const [posts, setPosts]           = useState([])
  const [loadingGroup, setLoadingGroup] = useState(true)
  const [loadingPosts, setLoadingPosts] = useState(true)
  const [hasMore, setHasMore]       = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [notFound, setNotFound]     = useState(false)

  const [showSettings, setShowSettings] = useState(false)
  const [showAllMembers, setShowAllMembers] = useState(false)

  const [joinBusy, setJoinBusy]     = useState(false)
  const [copied, setCopied]         = useState(false)
  const [draft, setDraft]           = useState('')
  const [posting, setPosting]       = useState(false)
  const [postError, setPostError]   = useState('')
  const textareaRef                 = useRef(null)

  // Load group
  useEffect(() => {
    setLoadingGroup(true)
    getHeaders().then(headers =>
      axios.get(`/api/groups/${id}`, { headers })
        .then(({ data }) => setGroupData(data))
        .catch(err => { if (err.response?.status === 404) setNotFound(true) })
        .finally(() => setLoadingGroup(false))
    )
  }, [id])

  // Load posts: top-level page first, then batch-fetch replies for those parents.
  useEffect(() => {
    setLoadingPosts(true)
    let cancelled = false
    ;(async () => {
      try {
        const headers = await getHeaders()
        const { data: page } = await axios.get(`/api/groups/${id}/posts?limit=30`, { headers })
        if (cancelled) return
        const topLevel = page?.posts ?? []
        let replies = []
        if (topLevel.length) {
          const { data: replyPage } = await axios.post(
            `/api/groups/${id}/posts/replies`,
            { parentIds: topLevel.map(p => p.id) },
            { headers }
          )
          if (cancelled) return
          replies = replyPage?.posts ?? []
        }
        setPosts([...topLevel, ...replies])
        setHasMore(Boolean(page?.hasMore))
        setTimeout(() => {
          const hash = window.location.hash
          if (hash) {
            const el = document.getElementById(hash.slice(1))
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('highlight-flash') }
          }
        }, 300)
      } catch {
        if (!cancelled) { setPosts([]); setHasMore(false) }
      } finally {
        if (!cancelled) setLoadingPosts(false)
      }
    })()
    return () => { cancelled = true }
  }, [id])

  // Load older top-level posts via the `before` cursor and merge their replies.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const headers = await getHeaders()
      const topLevel = posts.filter(p => !p.parentId)
      const oldest = topLevel[topLevel.length - 1]
      if (!oldest) return
      const { data: page } = await axios.get(
        `/api/groups/${id}/posts?limit=30&before=${encodeURIComponent(oldest.createdAt)}`,
        { headers }
      )
      const morePosts = page?.posts ?? []
      if (!morePosts.length) {
        setHasMore(Boolean(page?.hasMore))
        return
      }
      const { data: replyPage } = await axios.post(
        `/api/groups/${id}/posts/replies`,
        { parentIds: morePosts.map(p => p.id) },
        { headers }
      )
      const moreReplies = replyPage?.posts ?? []
      setPosts(prev => [...prev, ...morePosts, ...moreReplies])
      setHasMore(Boolean(page?.hasMore))
    } finally {
      setLoadingMore(false)
    }
  }, [id, posts, hasMore, loadingMore])

  // Re-scroll to the hash anchor when a notification click targets this page
  // without changing :id (hash-only change, or exact-same-URL re-click). The
  // initial-mount scroll is handled by the setTimeout inside the posts fetch
  // above; this listener covers the already-mounted case.
  // Same pattern as StoryReader.jsx and QuizPlayerPage.jsx. Extract to a
  // useScrollToHashOnEvent hook if a fourth page needs it.
  useEffect(() => {
    function onNotificationNavigate() {
      const hash = window.location.hash
      if (!hash) return
      const el = document.getElementById(hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('highlight-flash')
      }
    }
    window.addEventListener('verse:notification-navigate', onNotificationNavigate)
    return () => window.removeEventListener('verse:notification-navigate', onNotificationNavigate)
  }, [])

  // Realtime subscription for group posts
  useEffect(() => {
    if (!id) return

    // Cache of known author profiles to avoid redundant fetches
    const knownAuthors = {}

    async function fetchAuthor(userId) {
      if (knownAuthors[userId]) return knownAuthors[userId]
      const { data } = await supabase
        .from('profiles')
        .select('username, display_name, avatar_grad, avatar_url')
        .eq('id', userId)
        .maybeSingle()
      const author = data ? {
        username: data.username,
        displayName: data.display_name ?? data.username,
        avatarGrad: data.avatar_grad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)',
        avatarUrl: data.avatar_url ?? null,
      } : null
      if (author) knownAuthors[userId] = author
      return author
    }

    let channel
    try {
    channel = supabase
      .channel(`group-posts-${id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'group_posts',
        filter: `group_id=eq.${id}`,
      }, async (payload) => {
        const row = payload.new
        // Skip our own posts (already added optimistically)
        if (row.user_id === user?.id) return
        if (blockedIds.has(row.user_id)) return

        const author = await fetchAuthor(row.user_id)
        const post = {
          id: row.id,
          groupId: row.group_id,
          userId: row.user_id,
          content: row.content,
          parentId: row.parent_id ?? null,
          replyToId: row.reply_to_id ?? null,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
          author,
        }

        // Enrich replyToUsername if this is a reply-to-reply
        if (post.replyToId) {
          setPosts(prev => {
            const target = prev.find(p => p.id === post.replyToId)
            if (target?.author?.username) post.replyToUsername = target.author.username
            if (prev.some(p => p.id === post.id)) return prev
            return post.parentId ? [...prev, post] : [post, ...prev]
          })
        } else {
          setPosts(prev => {
            if (prev.some(p => p.id === post.id)) return prev
            return post.parentId ? [...prev, post] : [post, ...prev]
          })
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'group_posts',
        filter: `group_id=eq.${id}`,
      }, (payload) => {
        const row = payload.new
        setPosts(prev => prev.map(p => p.id === row.id ? {
          ...p,
          content: row.content,
          updatedAt: row.updated_at,
        } : p))
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'group_posts',
      }, (payload) => {
        setPosts(prev => prev.filter(p => p.id !== payload.old.id))
      })
      .subscribe()
    } catch (err) {
      console.warn('[GroupPage] Realtime subscription failed:', err)
    }

    return () => { if (channel) supabase.removeChannel(channel) }
  }, [id, user?.id])

  async function handleJoinToggle() {
    if (!user || joinBusy) return
    setJoinBusy(true)
    try {
      const headers = await getHeaders()
      if (groupData.group.isMember) {
        await axios.delete(`/api/groups/${id}/join`, { headers })
        setGroupData(d => ({
          ...d,
          group: { ...d.group, isMember: false, memberCount: d.group.memberCount - 1 },
        }))
      } else {
        await axios.post(`/api/groups/${id}/join`, {}, { headers })
        setGroupData(d => ({
          ...d,
          group: { ...d.group, isMember: true, memberCount: d.group.memberCount + 1 },
          recentMembers: [
            ...d.recentMembers,
            { username: currentUser.username, displayName: currentUser.displayName, avatarGrad: currentUser.grad, role: 'member' },
          ],
        }))
      }
    } catch (err) { console.error('[GroupPage] join/leave group failed:', err) }
    finally { setJoinBusy(false) }
  }

  async function handlePost(e) {
    e.preventDefault()
    if (!draft.trim() || posting) return
    setPosting(true)
    setPostError('')
    try {
      const headers = await getHeaders()
      const { data: post } = await axios.post(`/api/groups/${id}/posts`, { content: draft.trim() }, { headers })
      setPosts(ps => [post, ...ps])
      setDraft('')
      setGroupData(d => ({ ...d, group: { ...d.group, postCount: d.group.postCount + 1 } }))
    } catch (err) {
      setPostError(err.response?.data?.error ?? 'Failed to post.')
    } finally {
      setPosting(false)
    }
  }

  function handleGroupUpdated(updated) {
    setGroupData(d => ({ ...d, group: updated }))
    setShowSettings(false)
  }

  function handleGroupDeleted() {
    navigate('/community')
  }

  function handleMemberKicked(userId) {
    setGroupData(d => ({
      ...d,
      group: { ...d.group, memberCount: Math.max(0, d.group.memberCount - 1) },
      recentMembers: d.recentMembers.filter(m => m.userId !== userId),
    }))
  }

  async function handleDeletePost(postId) {
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/groups/${id}/posts/${postId}`, { headers })
      setPosts(ps => ps.filter(p => p.id !== postId))
      setGroupData(d => ({ ...d, group: { ...d.group, postCount: Math.max(0, d.group.postCount - 1) } }))
    } catch (err) { console.error('[GroupPage] delete post failed:', err) }
  }

  async function handleEditPost(postId, newContent) {
    const headers = await getHeaders()
    const { data: updated } = await axios.put(`/api/groups/${id}/posts/${postId}`, { content: newContent }, { headers })
    setPosts(ps => ps.map(p => p.id === postId ? updated : p))
  }

  async function handleReply(parentId, content, replyToId) {
    const headers = await getHeaders()
    const payload = { content, parent_id: parentId }
    if (replyToId) payload.reply_to_id = replyToId
    const { data: reply } = await axios.post(`/api/groups/${id}/posts`, payload, { headers })
    // Enrich with replyToUsername for immediate display
    if (replyToId) {
      const target = posts.find(p => p.id === replyToId)
      if (target?.author?.username) reply.replyToUsername = target.author.username
    }
    setPosts(ps => [...ps, reply])
    setGroupData(d => ({ ...d, group: { ...d.group, postCount: d.group.postCount + 1 } }))
  }

  if (notFound) return (
    <div className="group-page">
      <Navbar />
      <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--text-faint)' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>👥</div>
        <h2 style={{ marginBottom: 8 }}>Group not found</h2>
        <p style={{ marginBottom: 24 }}>This group doesn't exist or was deleted.</p>
        <Link to="/community" className="btn btn-primary">Browse groups</Link>
      </div>
    </div>
  )

  if (loadingGroup) return (
    <div className="group-page">
      <Navbar />
      <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--text-faint)' }}>Loading…</div>
    </div>
  )

  const { group, recentMembers } = groupData

  return (
    <div className="group-page">
      <SEO
        title={group.name}
        description={group.description?.slice(0, 160) || `${group.name} — a community group on Verse.`}
      />
      <Navbar />

      {/* ── Banner ── */}
      <div className="group-banner" style={{ background: group.coverUrl ? undefined : group.coverGrad }}>
        {group.coverUrl ? (
          <img src={group.coverUrl} alt="" className="group-banner__img" />
        ) : (
          <>
            <div className="group-banner__blob group-banner__blob--1" />
            <div className="group-banner__blob group-banner__blob--2" />
            <div className="group-banner__emoji">{group.coverEmoji}</div>
          </>
        )}
      </div>

      {/* ── Header ── */}
      <div className="group-header-wrap">
        <div className="group-header">
          <div className="group-header__info">
            <div className="group-header__meta">
              <span className="group-header__category">{group.category}</span>
              {group.isPrivate && <span className="group-header__private">🔒 Private</span>}
            </div>
            <h1 className="group-header__name">{decodeEntities(group.name)}</h1>
            {group.description && <p className="group-header__desc">{decodeEntities(group.description)}</p>}
            <div className="group-header__stats">
              <span>💬 {formatNum(group.postCount)} {group.postCount === 1 ? 'post' : 'posts'}</span>
            </div>
          </div>

          <div className="group-header__actions">
            <button
              className="btn story-header__action-btn"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
            >
              {copied ? '✓ Copied!' : '↗ Share'}
            </button>
            {user && !group.isOwner && (
              <button
                className={`btn ${group.isMember ? 'btn-outline group-header__leave-btn' : 'btn-primary'}`}
                onClick={handleJoinToggle}
                disabled={joinBusy}
              >
                {joinBusy ? '…' : group.isMember ? '✓ Joined' : '+ Join group'}
              </button>
            )}
            {group.isOwner && (
              <>
                <span className="group-header__owner-badge">Owner</span>
                <button className="btn btn-outline group-settings-btn" onClick={() => setShowSettings(true)}>
                  ⚙ Settings
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Settings modal ── */}
      {showSettings && (
        <GroupSettingsModal
          group={group}
          onClose={() => setShowSettings(false)}
          onUpdated={handleGroupUpdated}
          onDeleted={handleGroupDeleted}
          onMemberKicked={handleMemberKicked}
        />
      )}

      {showAllMembers && (
        <MembersModal
          groupId={group.id}
          memberCount={group.memberCount}
          onClose={() => setShowAllMembers(false)}
        />
      )}

      {/* ── Body ── */}
      <div className="group-body">

        {/* ── Posts feed ── */}
        <div className="group-feed">

          {/* Compose */}
          {user && group.isMember && (
            <form className="group-compose" onSubmit={handlePost}>
              <div
                className="group-compose__avatar"
                style={{ background: currentUser?.avatarUrl ? undefined : (currentUser?.grad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)') }}
              >
                {currentUser?.avatarUrl
                  ? <img src={currentUser.avatarUrl} alt={currentUser.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  : (currentUser?.initial ?? '?')
                }
              </div>
              <div className="group-compose__right">
                <textarea
                  ref={textareaRef}
                  className="group-compose__input"
                  placeholder={`Share something with ${group.name}…`}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  rows={3}
                  maxLength={5000}
                />
                {postError && <div className="auth-error" style={{ marginBottom: 8 }}>{postError}</div>}
                <div className="group-compose__footer">
                  <span className="group-compose__char">{draft.length}/5000</span>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ fontSize: '0.85rem', padding: '7px 18px' }}
                    disabled={!draft.trim() || posting}
                  >
                    {posting ? 'Posting…' : 'Post'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {!user && (
            <div className="group-join-prompt">
              <Link to="/login" className="btn btn-primary">Sign in to join the conversation</Link>
            </div>
          )}

          {user && !group.isMember && (
            <div className="group-join-prompt">
              <p>Join this group to post and interact with members.</p>
              <button className="btn btn-primary" onClick={handleJoinToggle} disabled={joinBusy}>
                + Join group
              </button>
            </div>
          )}

          {/* Posts */}
          {loadingPosts ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)' }}>Loading posts…</div>
          ) : posts.length === 0 ? (
            <div className="group-empty-posts">
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>💬</div>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>No posts yet</p>
              <p style={{ color: 'var(--text-faint)', fontSize: '0.88rem' }}>
                {group.isMember ? 'Be the first to post something!' : 'Join to start the conversation.'}
              </p>
            </div>
          ) : (
            <div className="group-posts">
              {posts.filter(post => !blockedIds.has(post.userId) && !post.parentId).map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  replies={posts.filter(r => r.parentId === post.id && !blockedIds.has(r.userId))}
                  allPosts={posts}
                  blockedIds={blockedIds}
                  currentUser={currentUser}
                  isMember={group.isMember}
                  isGroupOwner={group.isOwner}
                  onDelete={handleDeletePost}
                  onEdit={handleEditPost}
                  onReply={handleReply}
                />
              ))}
              {hasMore && (
                <button
                  className="btn btn-secondary"
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{ alignSelf: 'center', marginTop: 12 }}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Sidebar ── */}
        <aside className="group-sidebar">
          {/* Members preview */}
          <div className="group-sidebar__card">
            <h3 className="group-sidebar__title">Members · {formatNum(group.memberCount)}</h3>
            <div className="group-sidebar__members">
              {recentMembers.map(m => (
                <Link key={m.username} to={`/user/${m.username}`} className="group-member-row">
                  <div className="group-member-row__avatar" style={{ background: m.avatarUrl ? undefined : m.avatarGrad }}>
                    {m.avatarUrl
                      ? <img src={m.avatarUrl} alt={m.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      : (m.displayName?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div className="group-member-row__info">
                    <span className="group-member-row__name">{decodeEntities(m.displayName)}</span>
                    {m.role === 'owner' && <span className="group-member-row__role">Owner</span>}
                  </div>
                </Link>
              ))}
            </div>
            {group.memberCount > recentMembers.length && (
              <button
                className="group-sidebar__see-all"
                onClick={() => setShowAllMembers(true)}
              >
                See all {formatNum(group.memberCount)} members
              </button>
            )}
          </div>

          {/* Back link */}
          <Link to="/community" className="group-sidebar__back">
            ← All groups
          </Link>
        </aside>
      </div>
    </div>
  )
}

// ─── Group settings modal ──────────────────────────────────────────────────────

function GroupSettingsModal({ group, onClose, onUpdated, onDeleted, onMemberKicked }) {
  const [tab, setTab] = useState('general')
  const [form, setForm] = useState({
    name:        group.name,
    description: group.description ?? '',
    category:    group.category,
    coverGrad:   group.coverGrad,
    coverEmoji:  group.coverEmoji,
    isPrivate:   group.isPrivate,
  })
  const [saving, setSaving]           = useState(false)
  const [saveError, setSaveError]     = useState('')
  const [coverUploading, setCoverUploading] = useState(false)
  const [coverError, setCoverError]   = useState('')
  const [members, setMembers]         = useState([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [kicking, setKicking]         = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting]       = useState(false)

  // Invite tab
  const [inviteSearch, setInviteSearch]   = useState('')
  const [inviteResults, setInviteResults] = useState([])
  const [inviteLoading, setInviteLoading] = useState(false)
  const [adding, setAdding]               = useState(null) // userId being added
  const [addedSet, setAddedSet]           = useState(new Set())

  useEffect(() => {
    if (tab !== 'members') return
    setLoadingMembers(true)
    getHeaders().then(h =>
      axios.get(`/api/groups/${group.id}/members`, { headers: h })
        .then(({ data }) => setMembers(data))
        .catch(err => console.error('[GroupSettings] members fetch failed:', err))
        .finally(() => setLoadingMembers(false))
    )
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  function setField(key, val) { setForm(f => ({ ...f, [key]: val })); setSaveError('') }

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0]
    const input = e.target
    if (!file) return
    setCoverError('')
    setCoverUploading(true)
    try {
      let upload
      try {
        const blob = await resizeImage(file, { maxEdge: 1920 })
        upload = blob instanceof File ? blob : new File([blob], file.name, { type: blob.type })
      } catch {
        setCoverError(RESIZE_FALLBACK_MSG)
        return
      }
      const headers = await getHeaders()
      const formData = new FormData()
      formData.append('file', upload)
      const { data } = await axios.post(`/api/groups/${group.id}/cover`, formData, { headers })
      onUpdated({ ...group, coverUrl: data.url })
    } catch (err) {
      setCoverError(err.response?.data?.error ?? 'Upload failed')
    } finally {
      setCoverUploading(false)
      if (input) input.value = ''
    }
  }

  async function handleCoverRemove() {
    setCoverError('')
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/groups/${group.id}/cover`, { headers })
      onUpdated({ ...group, coverUrl: null })
    } catch (err) {
      setCoverError(err.response?.data?.error ?? 'Failed to remove cover')
    }
  }

  async function handleSave(e) {
    e.preventDefault()
    if (!form.name.trim()) { setSaveError('Name is required.'); return }
    setSaving(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.patch(`/api/groups/${group.id}`, form, { headers })
      onUpdated(data)
    } catch (err) {
      setSaveError(err.response?.data?.error ?? 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  async function handleKick(userId) {
    setKicking(userId)
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/groups/${group.id}/members/${userId}`, { headers })
      setMembers(ms => ms.filter(m => m.userId !== userId))
      onMemberKicked(userId)
    } catch (err) { console.error('[GroupSettings] kick member failed:', err) }
    finally { setKicking(null) }
  }

  useEffect(() => {
    if (tab !== 'invite') return
    if (!inviteSearch.trim()) { setInviteResults([]); return }
    const t = setTimeout(async () => {
      setInviteLoading(true)
      try {
        const headers = await getHeaders()
        const { data } = await axios.get(
          `/api/groups/${group.id}/invite/search?q=${encodeURIComponent(inviteSearch)}`,
          { headers }
        )
        setInviteResults(data)
      } catch (err) { console.error('[GroupSettings] invite search failed:', err) }
      finally { setInviteLoading(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [inviteSearch, tab]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleInvite(userId) {
    setAdding(userId)
    try {
      const headers = await getHeaders()
      await axios.post(`/api/groups/${group.id}/invite`, { userId }, { headers })
      setAddedSet(prev => new Set([...prev, userId]))
    } catch (err) { console.error('[GroupSettings] send invite failed:', err) }
    finally { setAdding(null) }
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/groups/${group.id}`, { headers })
      onDeleted()
    } catch (err) { console.error('[GroupSettings] delete group failed:', err); setDeleting(false) }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="create-group-modal group-settings-modal">
        <div className="create-group-modal__header">
          <h2>Group settings</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="settings-tabs">
          {[['general', 'General'], ['members', 'Members'], ['invite', 'Invite'], ['danger', 'Danger Zone']].map(([key, label]) => (
            <button
              key={key}
              className={`settings-tab${tab === key ? ' settings-tab--active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── General ── */}
        {tab === 'general' && (
          <form className="create-group-modal__form" onSubmit={handleSave}>
            <div className="create-group-modal__preview" style={{ background: form.coverGrad }}>
              <span style={{ fontSize: '3rem' }}>{form.coverEmoji}</span>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                {form.name || 'Group name'}
              </span>
            </div>

            <div className="create-group-modal__field">
              <label>Cover photo</label>
              {group.coverUrl && (
                <div className="settings-cover-preview">
                  <img src={group.coverUrl} alt="Cover" />
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label className="btn btn-outline" style={{ cursor: coverUploading ? 'default' : 'pointer', fontSize: '0.82rem', padding: '6px 14px', opacity: coverUploading ? 0.6 : 1, pointerEvents: coverUploading ? 'none' : 'auto' }}>
                  {coverUploading ? 'Uploading…' : 'Upload cover'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleCoverUpload} disabled={coverUploading} />
                </label>
                {group.coverUrl && (
                  <button type="button" className="btn" disabled={coverUploading} style={{ fontSize: '0.82rem', padding: '6px 14px', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: coverUploading ? 'default' : 'pointer', opacity: coverUploading ? 0.5 : 1 }} onClick={handleCoverRemove}>
                    Remove
                  </button>
                )}
              </div>
              {coverError && <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: 4 }}>{coverError}</p>}
            </div>

            <div className="create-group-modal__field">
              <label>Group name</label>
              <input className="auth-input" value={form.name} onChange={e => setField('name', e.target.value)} maxLength={60} />
            </div>

            <div className="create-group-modal__field">
              <label>Description <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional)</span></label>
              <textarea className="auth-input create-group-modal__textarea" value={form.description} onChange={e => setField('description', e.target.value)} maxLength={300} rows={3} />
            </div>

            <div className="create-group-modal__field">
              <label>Category</label>
              <select className="auth-input" value={form.category} onChange={e => setField('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>

            <div className="create-group-modal__field">
              <label>Cover colour</label>
              <div className="create-group-modal__swatches">
                {GRADIENTS.map(g => (
                  <button key={g} type="button" className={`swatch${form.coverGrad === g ? ' swatch--active' : ''}`} style={{ background: g }} onClick={() => setField('coverGrad', g)} />
                ))}
              </div>
            </div>

            <div className="create-group-modal__field">
              <label>Icon</label>
              <div className="create-group-modal__emojis">
                {EMOJIS.map(em => (
                  <button key={em} type="button" className={`emoji-pick${form.coverEmoji === em ? ' emoji-pick--active' : ''}`} onClick={() => setField('coverEmoji', em)}>
                    {em}
                  </button>
                ))}
              </div>
            </div>

            <label className="create-group-modal__private">
              <input type="checkbox" checked={form.isPrivate} onChange={e => setField('isPrivate', e.target.checked)} />
              <span>Private group <span style={{ color: 'var(--text-faint)', fontWeight: 400, fontSize: '0.82rem' }}>(invite only)</span></span>
            </label>

            {saveError && <div className="auth-error">{saveError}</div>}

            <div className="create-group-modal__footer">
              <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </form>
        )}

        {/* ── Members ── */}
        {tab === 'members' && (
          <div className="settings-members">
            {loadingMembers ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)' }}>Loading…</div>
            ) : members.length === 0 ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)' }}>No members found.</div>
            ) : (
              <div className="settings-members__list">
                {members.map(m => (
                  <div key={m.userId} className="settings-member-row">
                    <Link to={`/user/${m.username}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
                      <div className="group-member-row__avatar" style={{ background: m.avatarGrad }}>
                        {(m.displayName?.[0] ?? '?').toUpperCase()}
                      </div>
                    </Link>
                    <div className="settings-member-row__info">
                      <Link to={`/user/${m.username}`} className="group-member-row__name" style={{ textDecoration: 'none', color: 'inherit' }}>{decodeEntities(m.displayName)}</Link>
                      {m.role === 'owner' && (
                        <span className="group-member-row__role">Owner</span>
                      )}
                    </div>
                    {m.role !== 'owner' && (
                      <button
                        className="settings-kick-btn"
                        disabled={kicking === m.userId}
                        onClick={() => handleKick(m.userId)}
                      >
                        {kicking === m.userId ? '…' : 'Kick'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Invite ── */}
        {tab === 'invite' && (
          <div className="settings-invite">
            <p className="settings-invite__hint">
              Search for users by username and add them directly to the group.
            </p>
            <input
              className="auth-input"
              placeholder="Search by username…"
              value={inviteSearch}
              onChange={e => { setInviteSearch(e.target.value); setInviteResults([]) }}
              autoFocus
            />
            <div className="settings-invite__results">
              {inviteLoading && (
                <div className="settings-invite__status">Searching…</div>
              )}
              {!inviteLoading && inviteSearch.trim() && inviteResults.length === 0 && (
                <div className="settings-invite__status">No users found.</div>
              )}
              {inviteResults.map(u => {
                const alreadyAdded = addedSet.has(u.id)
                return (
                  <div key={u.id} className="settings-invite-row">
                    <Link to={`/user/${u.username}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
                      <div
                        className="group-member-row__avatar"
                        style={{ background: u.avatarUrl ? undefined : u.avatarGrad }}
                      >
                        {u.avatarUrl
                          ? <img src={u.avatarUrl} alt={u.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                          : (u.displayName?.[0] ?? '?').toUpperCase()
                        }
                      </div>
                    </Link>
                    <div className="settings-invite-row__info">
                      <Link to={`/user/${u.username}`} className="group-member-row__name" style={{ textDecoration: 'none', color: 'inherit' }}>{decodeEntities(u.displayName)}</Link>
                      <span className="settings-invite-row__username">@{u.username}</span>
                    </div>
                    {u.isMember || alreadyAdded ? (
                      <span className="settings-invite-row__badge">Member</span>
                    ) : (
                      <button
                        className="settings-invite-row__btn"
                        disabled={adding === u.id}
                        onClick={() => handleInvite(u.id)}
                      >
                        {adding === u.id ? '…' : 'Add'}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ── Danger ── */}
        {tab === 'danger' && (
          <div className="settings-danger">
            <div className="settings-danger__card">
              <div>
                <strong>Delete this group</strong>
                <p>Permanently deletes the group, all posts, and removes all members. This cannot be undone.</p>
              </div>
              {!deleteConfirm ? (
                <button className="settings-danger__btn" onClick={() => setDeleteConfirm(true)}>
                  Delete group
                </button>
              ) : (
                <div className="settings-danger__confirm">
                  <p>Are you sure? This is permanent.</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-outline" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                    <button className="settings-danger__btn" onClick={handleDelete} disabled={deleting}>
                      {deleting ? 'Deleting…' : 'Yes, delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Read-only "all members" modal (non-owner click-through) ──────────────────

function MembersModal({ groupId, memberCount, onClose }) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    getHeaders()
      .then(headers => axios.get(`/api/groups/${groupId}/members`, { headers }))
      .then(({ data }) => { if (!cancelled) setMembers(data) })
      .catch(err => {
        if (cancelled) return
        if (err.response?.status === 403) {
          setError("You must be a member to view this group's members.")
        } else {
          setError('Could not load members. Please try again.')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [groupId])

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="create-group-modal group-members-modal">
        <div className="create-group-modal__header">
          <h2>All members · {formatNum(memberCount)}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="group-members-modal__list">
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)' }}>
              Loading…
            </div>
          ) : error ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
              {error}
            </div>
          ) : members.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)' }}>
              No members found.
            </div>
          ) : (
            members.map(m => (
              <Link
                key={m.userId}
                to={`/user/${m.username}`}
                className="group-member-row"
                onClick={onClose}
              >
                <div
                  className="group-member-row__avatar"
                  style={{ background: m.avatarUrl ? undefined : m.avatarGrad }}
                >
                  {m.avatarUrl
                    ? <img src={m.avatarUrl} alt={m.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : (m.displayName?.[0] ?? '?').toUpperCase()}
                </div>
                <div className="group-member-row__info">
                  <span className="group-member-row__name">{decodeEntities(m.displayName)}</span>
                  {m.role === 'owner' && <span className="group-member-row__role">Owner</span>}
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Post card ─────────────────────────────────────────────────────────────────

function PostCard({ post, replies = [], allPosts = [], blockedIds, currentUser, isMember, isGroupOwner, onDelete, onEdit, onReply }) {
  const [menuOpen, setMenuOpen]       = useState(false)
  const [editing, setEditing]         = useState(false)
  const [editText, setEditText]       = useState(post.content)
  const [saving, setSaving]           = useState(false)
  const [editError, setEditError]     = useState('')
  const [reporting, setReporting]     = useState(false)
  const [replying, setReplying]       = useState(false)
  const [replyText, setReplyText]     = useState('')
  const [replySending, setReplySending] = useState(false)
  const [replyError, setReplyError]   = useState('')
  const [showAllReplies, setShowAllReplies] = useState(false)
  const menuRef = useRef(null)
  const isOwn = currentUser?.username === post.author?.username

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  async function handleSave() {
    if (!editText.trim() || saving) return
    setSaving(true)
    setEditError('')
    try {
      await onEdit(post.id, editText)
      setEditing(false)
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    setEditText(post.content)
    setEditing(false)
    setEditError('')
  }

  async function handleReplySubmit(e) {
    e.preventDefault()
    if (!replyText.trim() || replySending) return
    setReplySending(true)
    setReplyError('')
    try {
      await onReply(post.id, replyText.trim())
      setReplyText('')
      setReplying(false)
      setShowAllReplies(true)
    } catch (err) {
      setReplyError(err.response?.data?.error || 'Failed to reply.')
    } finally {
      setReplySending(false)
    }
  }

  const sortedReplies = [...replies].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  return (
    <div id={`post-${post.id}`} className="group-post">
      <Link
        to={`/user/${post.author?.username}`}
        className="group-post__avatar"
        style={{ background: post.author?.avatarUrl ? undefined : post.author?.avatarGrad }}
      >
        {post.author?.avatarUrl
          ? <img src={post.author.avatarUrl} alt={post.author.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          : (post.author?.displayName?.[0] ?? '?').toUpperCase()
        }
      </Link>
      <div className="group-post__body">
        <div className="group-post__header">
          <Link to={`/user/${post.author?.username}`} className="group-post__author">
            {decodeEntities(post.author?.displayName) ?? 'Unknown'}
          </Link>
          <span className="group-post__time">{timeAgo(post.createdAt)}</span>
          {currentUser && !editing && (
            <div className="group-post__menu-wrap" ref={menuRef}>
              <button className="group-post__menu-btn" onClick={() => setMenuOpen(v => !v)}>
                •••
              </button>
              {menuOpen && (
                <div className="group-post__menu">
                  {isOwn && (
                    <button onClick={() => { setMenuOpen(false); setEditing(true) }}>
                      Edit
                    </button>
                  )}
                  {(isOwn || isGroupOwner) && (
                    <button className="danger" onClick={() => { setMenuOpen(false); onDelete(post.id) }}>
                      Delete
                    </button>
                  )}
                  {!isOwn && (
                    <button onClick={() => { setMenuOpen(false); setReporting(true) }}>
                      Report
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {editing ? (
          <div className="group-post__edit">
            <textarea
              className="group-compose__input"
              value={editText}
              onChange={e => { setEditText(e.target.value); setEditError('') }}
              rows={3}
              maxLength={5000}
              autoFocus
            />
            {editError && <div className="auth-error" style={{ marginBottom: 6 }}>{editError}</div>}
            <div className="group-post__edit-actions">
              <button
                className="btn btn-primary"
                style={{ fontSize: '0.82rem', padding: '6px 16px' }}
                onClick={handleSave}
                disabled={!editText.trim() || saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                className="btn"
                style={{ fontSize: '0.82rem', padding: '6px 16px' }}
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="group-post__content">{decodeEntities(post.content)}</p>
        )}

        {/* Reply action */}
        {!editing && currentUser && isMember && (
          <div className="group-post__actions">
            <button className="group-post__reply-btn" onClick={() => setReplying(v => !v)}>
              Reply{replies.length > 0 ? ` (${replies.length})` : ''}
            </button>
          </div>
        )}

        {/* Replies */}
        {sortedReplies.length > 0 && (
          <div className="group-post__replies">
            {!showAllReplies && sortedReplies.length > 2 && (
              <button className="comment__show-more" onClick={() => setShowAllReplies(true)}>
                Show {sortedReplies.length - 2} more {sortedReplies.length - 2 === 1 ? 'reply' : 'replies'}
              </button>
            )}
            {showAllReplies && sortedReplies.length > 2 && (
              <button className="comment__show-more" onClick={() => setShowAllReplies(false)}>
                Show less
              </button>
            )}
            {(showAllReplies ? sortedReplies : sortedReplies.slice(-2)).map(reply => (
              <ReplyCard
                key={reply.id}
                reply={reply}
                nestedReplies={allPosts.filter(r => r.parentId === reply.id && !blockedIds?.has(r.userId))}
                allPosts={allPosts}
                blockedIds={blockedIds}
                currentUser={currentUser}
                isMember={isMember}
                isGroupOwner={isGroupOwner}
                onDelete={onDelete}
                onEdit={onEdit}
                onReply={onReply}
              />
            ))}
          </div>
        )}

        {/* Reply composer */}
        {replying && (
          <form className="group-post__reply-compose" onSubmit={handleReplySubmit}>
            <textarea
              className="group-compose__input"
              placeholder={`Reply to ${post.author?.displayName ?? 'this post'}…`}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              rows={2}
              maxLength={5000}
              autoFocus
            />
            {replyError && <div className="auth-error" style={{ fontSize: '0.8rem', marginBottom: 4 }}>{replyError}</div>}
            <div className="group-post__edit-actions">
              <button
                type="submit"
                className="btn btn-primary"
                style={{ fontSize: '0.8rem', padding: '5px 14px' }}
                disabled={!replyText.trim() || replySending}
              >
                {replySending ? 'Replying…' : 'Reply'}
              </button>
              <button
                type="button"
                className="btn"
                style={{ fontSize: '0.8rem', padding: '5px 14px' }}
                onClick={() => { setReplying(false); setReplyText(''); setReplyError('') }}
                disabled={replySending}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
      {reporting && (
        <ReportModal
          contentType="group_post"
          contentId={post.id}
          onClose={() => setReporting(false)}
        />
      )}
    </div>
  )
}

function ReplyCard({ reply, nestedReplies = [], allPosts = [], blockedIds, currentUser, isMember, isGroupOwner, onDelete, onEdit, onReply }) {
  const [menuOpen, setMenuOpen]   = useState(false)
  const [editing, setEditing]     = useState(false)
  const [editText, setEditText]   = useState(reply.content)
  const [saving, setSaving]       = useState(false)
  const [editError, setEditError] = useState('')
  const [reporting, setReporting] = useState(false)
  const [replying, setReplying]   = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replySending, setReplySending] = useState(false)
  const [replyError, setReplyError]     = useState('')
  const [showAllNested, setShowAllNested] = useState(false)
  const menuRef = useRef(null)
  const isOwn = currentUser?.username === reply.author?.username

  const sortedNested = [...nestedReplies].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  useEffect(() => {
    if (!menuOpen) return
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  async function handleSave() {
    if (!editText.trim() || saving) return
    setSaving(true)
    setEditError('')
    try {
      await onEdit(reply.id, editText)
      setEditing(false)
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  async function handleReplySubmit(e) {
    e.preventDefault()
    if (!replyText.trim() || replySending) return
    setReplySending(true)
    setReplyError('')
    try {
      // If this reply already has a parent, flatten under the same parent (max 2 levels)
      const targetParent = reply.parentId ? reply.parentId : reply.id
      // If replying to a reply (not the top-level parent), pass the actual target for @mention
      const replyToId = reply.parentId ? reply.id : undefined
      await onReply(targetParent, replyText.trim(), replyToId)
      setReplyText('')
      setReplying(false)
      setShowAllNested(true)
    } catch (err) {
      setReplyError(err.response?.data?.error || 'Failed to reply.')
    } finally {
      setReplySending(false)
    }
  }

  return (
    <div id={`post-${reply.id}`} className="group-reply">
      <Link
        to={`/user/${reply.author?.username}`}
        className="group-reply__avatar"
        style={{ background: reply.author?.avatarUrl ? undefined : reply.author?.avatarGrad }}
      >
        {reply.author?.avatarUrl
          ? <img src={reply.author.avatarUrl} alt={reply.author.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          : (reply.author?.displayName?.[0] ?? '?').toUpperCase()
        }
      </Link>
      <div className="group-reply__body">
        <div className="group-post__header">
          <Link to={`/user/${reply.author?.username}`} className="group-post__author" style={{ fontSize: '0.85rem' }}>
            {decodeEntities(reply.author?.displayName) ?? 'Unknown'}
          </Link>
          {reply.replyToUsername && (
            <Link to={`/user/${reply.replyToUsername}`} style={{ fontSize: '0.76rem', color: 'var(--text-faint)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              ↩ <span style={{ color: 'var(--purple)' }}>@{reply.replyToUsername}</span>
            </Link>
          )}
          <span className="group-post__time">{timeAgo(reply.createdAt)}</span>
          {currentUser && !editing && (
            <div className="group-post__menu-wrap" ref={menuRef}>
              <button className="group-post__menu-btn" onClick={() => setMenuOpen(v => !v)}>
                •••
              </button>
              {menuOpen && (
                <div className="group-post__menu">
                  {isOwn && (
                    <button onClick={() => { setMenuOpen(false); setEditing(true) }}>Edit</button>
                  )}
                  {(isOwn || isGroupOwner) && (
                    <button className="danger" onClick={() => { setMenuOpen(false); onDelete(reply.id) }}>Delete</button>
                  )}
                  {!isOwn && (
                    <button onClick={() => { setMenuOpen(false); setReporting(true) }}>Report</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <div className="group-post__edit">
            <textarea
              className="group-compose__input"
              value={editText}
              onChange={e => { setEditText(e.target.value); setEditError('') }}
              rows={2}
              maxLength={5000}
              autoFocus
            />
            {editError && <div className="auth-error" style={{ fontSize: '0.8rem', marginBottom: 4 }}>{editError}</div>}
            <div className="group-post__edit-actions">
              <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={handleSave} disabled={!editText.trim() || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={() => { setEditText(reply.content); setEditing(false) }} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="group-post__content" style={{ fontSize: '0.9rem' }}>{decodeEntities(reply.content)}</p>
        )}

        {/* Reply action */}
        {!editing && currentUser && isMember && (
          <div className="group-post__actions">
            <button className="group-post__reply-btn" onClick={() => setReplying(v => !v)}>
              Reply{nestedReplies.length > 0 ? ` (${nestedReplies.length})` : ''}
            </button>
          </div>
        )}

        {/* Nested replies */}
        {sortedNested.length > 0 && (
          <div className="group-post__replies group-post__replies--nested">
            {!showAllNested && sortedNested.length > 2 && (
              <button className="comment__show-more" onClick={() => setShowAllNested(true)}>
                Show {sortedNested.length - 2} more {sortedNested.length - 2 === 1 ? 'reply' : 'replies'}
              </button>
            )}
            {(showAllNested ? sortedNested : sortedNested.slice(-2)).map(nested => (
              <ReplyCard
                key={nested.id}
                reply={nested}
                nestedReplies={[]}
                allPosts={allPosts}
                blockedIds={blockedIds}
                currentUser={currentUser}
                isMember={isMember}
                isGroupOwner={isGroupOwner}
                onDelete={onDelete}
                onEdit={onEdit}
                onReply={onReply}
              />
            ))}
          </div>
        )}

        {/* Reply composer */}
        {replying && (
          <form className="group-post__reply-compose" onSubmit={handleReplySubmit}>
            <textarea
              className="group-compose__input"
              placeholder={`Reply to ${reply.author?.displayName ?? 'this reply'}…`}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              rows={2}
              maxLength={5000}
              autoFocus
            />
            {replyError && <div className="auth-error" style={{ fontSize: '0.8rem', marginBottom: 4 }}>{replyError}</div>}
            <div className="group-post__edit-actions">
              <button
                type="submit"
                className="btn btn-primary"
                style={{ fontSize: '0.8rem', padding: '5px 14px' }}
                disabled={!replyText.trim() || replySending}
              >
                {replySending ? 'Replying…' : 'Reply'}
              </button>
              <button
                type="button"
                className="btn"
                style={{ fontSize: '0.8rem', padding: '5px 14px' }}
                onClick={() => { setReplying(false); setReplyText(''); setReplyError('') }}
                disabled={replySending}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
      {reporting && (
        <ReportModal contentType="group_post" contentId={reply.id} onClose={() => setReporting(false)} />
      )}
    </div>
  )
}

function NestedReplyCard({ reply, currentUser, isMember, isGroupOwner, onDelete, onEdit, onReply }) {
  const [menuOpen, setMenuOpen]   = useState(false)
  const [editing, setEditing]     = useState(false)
  const [editText, setEditText]   = useState(reply.content)
  const [saving, setSaving]       = useState(false)
  const [editError, setEditError] = useState('')
  const [reporting, setReporting] = useState(false)
  const [replying, setReplying]   = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replySending, setReplySending] = useState(false)
  const [replyError, setReplyError]     = useState('')
  const menuRef = useRef(null)
  const isOwn = currentUser?.username === reply.author?.username

  useEffect(() => {
    if (!menuOpen) return
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  async function handleSave() {
    if (!editText.trim() || saving) return
    setSaving(true)
    setEditError('')
    try {
      await onEdit(reply.id, editText)
      setEditing(false)
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div id={`post-${reply.id}`} className="group-reply group-reply--nested">
      <Link
        to={`/user/${reply.author?.username}`}
        className="group-reply__avatar group-reply__avatar--sm"
        style={{ background: reply.author?.avatarUrl ? undefined : reply.author?.avatarGrad }}
      >
        {reply.author?.avatarUrl
          ? <img src={reply.author.avatarUrl} alt={reply.author.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          : (reply.author?.displayName?.[0] ?? '?').toUpperCase()
        }
      </Link>
      <div className="group-reply__body">
        <div className="group-post__header">
          <Link to={`/user/${reply.author?.username}`} className="group-post__author" style={{ fontSize: '0.82rem' }}>
            {decodeEntities(reply.author?.displayName) ?? 'Unknown'}
          </Link>
          {reply.replyToUsername && (
            <Link to={`/user/${reply.replyToUsername}`} style={{ fontSize: '0.74rem', color: 'var(--text-faint)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              ↩ <span style={{ color: 'var(--purple)' }}>@{reply.replyToUsername}</span>
            </Link>
          )}
          <span className="group-post__time">{timeAgo(reply.createdAt)}</span>
          {currentUser && !editing && (
            <div className="group-post__menu-wrap" ref={menuRef}>
              <button className="group-post__menu-btn" onClick={() => setMenuOpen(v => !v)}>
                •••
              </button>
              {menuOpen && (
                <div className="group-post__menu">
                  {isOwn && (
                    <button onClick={() => { setMenuOpen(false); setEditing(true) }}>Edit</button>
                  )}
                  {(isOwn || isGroupOwner) && (
                    <button className="danger" onClick={() => { setMenuOpen(false); onDelete(reply.id) }}>Delete</button>
                  )}
                  {!isOwn && (
                    <button onClick={() => { setMenuOpen(false); setReporting(true) }}>Report</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <div className="group-post__edit">
            <textarea
              className="group-compose__input"
              value={editText}
              onChange={e => { setEditText(e.target.value); setEditError('') }}
              rows={2}
              maxLength={5000}
              autoFocus
            />
            {editError && <div className="auth-error" style={{ fontSize: '0.8rem', marginBottom: 4 }}>{editError}</div>}
            <div className="group-post__edit-actions">
              <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={handleSave} disabled={!editText.trim() || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={() => { setEditText(reply.content); setEditing(false) }} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="group-post__content" style={{ fontSize: '0.88rem' }}>{decodeEntities(reply.content)}</p>
        )}

        {/* Reply action */}
        {!editing && currentUser && isMember && (
          <div className="group-post__actions">
            <button className="group-post__reply-btn" onClick={() => setReplying(v => !v)}>
              Reply
            </button>
          </div>
        )}

        {/* Reply composer */}
        {replying && (
          <form className="group-post__reply-compose" onSubmit={async (e) => {
            e.preventDefault()
            if (!replyText.trim() || replySending) return
            setReplySending(true)
            setReplyError('')
            try {
              await onReply(reply.parentId, replyText.trim(), reply.id)
              setReplyText('')
              setReplying(false)
            } catch (err) {
              setReplyError(err.response?.data?.error || 'Failed to reply.')
            } finally {
              setReplySending(false)
            }
          }}>
            <textarea
              className="group-compose__input"
              placeholder={`Reply to ${reply.author?.displayName ?? 'this reply'}…`}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              rows={2}
              maxLength={5000}
              autoFocus
            />
            {replyError && <div className="auth-error" style={{ fontSize: '0.8rem', marginBottom: 4 }}>{replyError}</div>}
            <div className="group-post__edit-actions">
              <button type="submit" className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '5px 14px' }} disabled={!replyText.trim() || replySending}>
                {replySending ? 'Replying…' : 'Reply'}
              </button>
              <button type="button" className="btn" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={() => { setReplying(false); setReplyText(''); setReplyError('') }} disabled={replySending}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
      {reporting && (
        <ReportModal contentType="group_post" contentId={reply.id} onClose={() => setReporting(false)} />
      )}
    </div>
  )
}
